import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Pagination,
  Paper,
  IconButton,
  Toolbar,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete,
  Edit,
  Search,
  Refresh,
  Person,
  Phone,
  Email,
  Notifications,
} from '@mui/icons-material';
import * as Actions from 'actions';
import SendNotificationDialog from '../SendNotificationDialog';

const ContactList = (props) => {
  const { 
    onEdit, 
    contacts = { list: [], total: 0, skip: 0, limit: 50 }, 
    isLoading, 
    reqStatus, 
    getContacts, 
    deleteContact, 
    deleteMultipleContacts 
  } = props;
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  const loadContacts = useCallback(() => {
    getContacts({
      search: searchTerm || "",
      skip: page * rowsPerPage,
      limit: rowsPerPage,
    });
  }, [getContacts, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Refresh list after delete operations
  useEffect(() => {
    if (
      reqStatus === 'SELLER_DELETE_CONTACT_SUCCESS' ||
      reqStatus === 'SELLER_DELETE_MULTIPLE_CONTACTS_SUCCESS' ||
      reqStatus === 'SELLER_CREATE_CONTACT_SUCCESS'
    ) {
      loadContacts();
    }
  }, [reqStatus, loadContacts]);

  // Clear selected contacts after sending notification
  useEffect(() => {
    if (reqStatus === 'SELLER_SEND_NOTIFICATION_TO_CONTACTS_SUCCESS') {
      setSelectedContacts([]);
      setShowNotificationDialog(false);
    }
  }, [reqStatus]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    if (event.key === 'Enter' || event.type === 'click') {
      setPage(0);
      loadContacts();
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = contacts.list.map((c) => c._id);
      setSelectedContacts(allIds);
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectOne = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter((id) => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  const handleDeleteSelected = () => {
    const count = selectedContacts.length;
    if (window.confirm(`Are you sure you want to delete ${count} contact(s)?`)) {
      deleteMultipleContacts(selectedContacts);
      setSelectedContacts([]);
    }
  };

  const handleDeleteSingle = (contactId, contactName) => {
    if (window.confirm(`Are you sure you want to delete "${contactName}"?`)) {
      deleteContact(contactId);
    }
  };

  const handleEdit = (contact) => {
    if (onEdit) {
      onEdit(contact);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleOpenNotificationDialog = () => {
    if (selectedContacts.length === 0) {
      alert('Please select at least one contact');
      return;
    }
    setShowNotificationDialog(true);
  };

  const handleCloseNotificationDialog = () => {
    setShowNotificationDialog(false);
  };

  const getSelectedContactObjects = () => {
    return contacts.list.filter(contact => selectedContacts.includes(contact._id));
  };

  const totalPages = Math.ceil(contacts.total / rowsPerPage);

  return (
    <Box>
      {/* Toolbar */}
      <Toolbar 
        sx={{ 
          pl: { xs: 1, sm: 2 }, 
          pr: { xs: 1, sm: 1 },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 0 },
          py: { xs: 2, sm: 1 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, flex: { sm: '1 1 100%' } }}>
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            id="tableTitle"
            component="div"
          >
            Contacts ({contacts.total})
          </Typography>

          {selectedContacts.length > 0 && (
            <Chip
              label={`${selectedContacts.length} selected`}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          )}
        </Box>

        {selectedContacts.length > 0 ? (
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={!isMobile && <Notifications />}
              onClick={handleOpenNotificationDialog}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {isMobile ? <><Notifications sx={{ mr: 0.5 }} /> Notify</> : "Send Notification"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={!isMobile && <Delete />}
              onClick={handleDeleteSelected}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {isMobile ? <><Delete sx={{ mr: 0.5 }} /> Delete</> : "Delete Selected"}
            </Button>
          </Stack>
        ) : (
          <IconButton onClick={loadContacts} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <Refresh />
          </IconButton>
        )}
      </Toolbar>

      {/* Search Bar */}
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        <TextField
          fullWidth
          size={isMobile ? "small" : "medium"}
          placeholder={isMobile ? "Search..." : "Search contacts by name or mobile..."}
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={handleSearchSubmit}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: !isMobile && (
              <InputAdornment position="end">
                <Button onClick={handleSearchSubmit} size="small">Search</Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Contacts Display - Mobile Cards or Desktop Table */}
      {!isLoading && (
        <>
          {/* Mobile Card View */}
          {isMobile ? (
            <Box sx={{ px: 1 }}>
              {!contacts.list || contacts.list.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography color="text.secondary">
                    No contacts found
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload a contact file or add contacts manually
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1}>
                  {contacts.list.map((contact) => {
                    const isSelected = selectedContacts.includes(contact._id);
                    return (
                      <Card 
                        key={contact._id}
                        variant="outlined"
                        sx={{ 
                          backgroundColor: isSelected ? 'action.selected' : 'background.paper'
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectOne(contact._id)}
                              sx={{ p: 0, mt: -0.5 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Person sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
                                <Typography 
                                  variant="body2" 
                                  fontWeight="medium"
                                  sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {contact.name}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Phone sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {contact.mobile}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ pt: 0, px: 2, pb: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(contact)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeleteSingle(contact._id, contact.name)
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </CardActions>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Box>
          ) : (
            /* Desktop Table View */
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedContacts.length > 0 &&
                          selectedContacts.length < contacts.list.length
                        }
                        checked={
                          contacts.list.length > 0 &&
                          selectedContacts.length === contacts.list.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!contacts.list || contacts.list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Box sx={{ py: 3 }}>
                          <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="text.secondary">
                            No contacts found
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Upload a contact file or add contacts manually
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.list.map((contact) => {
                      const isSelected = selectedContacts.includes(contact._id);
                      return (
                        <TableRow
                          key={contact._id}
                          hover
                          selected={isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectOne(contact._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Person sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">{contact.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Phone sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                              <Typography variant="body2">
                                {contact.mobile}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(contact)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteSingle(contact._id, contact.name)
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 2 } }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            size={isMobile ? "small" : "medium"}
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={isMobile ? 1 : 1}
          />
        </Box>
      )}

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={showNotificationDialog}
        onClose={handleCloseNotificationDialog}
        selectedContacts={getSelectedContactObjects()}
      />
    </Box>
  );
};

const _ContactList = connect(
  ({ sellerContacts }) => ({
    contacts: sellerContacts.contacts,
    isLoading: sellerContacts.isLoading,
    reqStatus: sellerContacts.reqStatus,
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ContactList);

export { _ContactList as default };
